/**
 * Realtime collab socket client (Collab Phase 8).
 *
 * Owns the WebSocket lifecycle and frame dispatch; delegates op ordering to
 * op-capture. Zero UI: it emits eventBus events (`collab:connected`,
 * `collab:disconnected`, `collab:peers-changed`, `collab:presence`,
 * `collab:lock-state`, `collab:lock-result`, …) and calls injected callbacks
 * for the two decisions the shell owns (divergence adoption, save-notify).
 *
 * Transport: wss://<api-origin>/api/projects/<projectId>/collab, subprotocols
 * ['pb.collab.v1'] (cookie auth) or ['pb.collab.v1','pb.token.<jwt>'] (the
 * wrangler-dev fallback — browsers can't set Authorization on a WS upgrade).
 * Keepalive: a bare {"t":"ping"} every 30 s (the DO auto-responds without
 * waking). Reconnect: exponential backoff 1 s→30 s + jitter, replaying via the
 * persisted lastSeq (`pb-collab-seq:<projectId>`); a hash-mismatch / snapshot
 * welcome routes through the divergence callback.
 *
 * Fully injectable (socketFactory, timers, store, callbacks) for the
 * fake-socket + in-memory hub tests.
 */

import { eventBus as defaultEventBus } from '../core/events.js';
import { store as defaultStore } from '../core/state.js';
import {
  COLLAB_PROTOCOL_VERSION,
  COLLAB_SUBPROTOCOL,
  COLLAB_TOKEN_SUBPROTOCOL_PREFIX,
  EDITABLE_ID_REPAIR_CAPABILITY,
  CLOSE_CODES,
  LIMITS,
  stateHashOf
} from '../shared/collab-protocol.js';
import { canonicalCollabFrame, canonicalCollabStateHashOf } from '../shared/collab-asset-state.js';
import { createOpCapture } from './op-capture.js';
import {
  LIFECYCLE,
  LIFECYCLE_OPERATION,
  classifyProjectSignal
} from '../core/project-lifecycle.js';

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30_000;
const SYNC_INTERVAL_MS = 30_000;

function seqStorageKey(projectId) {
  return `pb-collab-seq:${projectId}`;
}

export function coarseDeviceLabel(nav = globalThis.navigator) {
  const platform = String(nav?.userAgentData?.platform || nav?.platform || '').toLowerCase();
  const ua = String(nav?.userAgent || '').toLowerCase();
  const device = /iphone|ipad/.test(`${platform} ${ua}`)
    ? 'iPhone or iPad'
    : /android/.test(`${platform} ${ua}`)
      ? 'Android device'
      : platform.includes('mac')
        ? 'Mac'
        : platform.includes('win')
          ? 'Windows computer'
          : platform.includes('linux')
            ? 'Linux computer'
            : 'computer';
  const browser = ua.includes('firefox')
    ? 'Firefox'
    : ua.includes('edg/')
      ? 'Edge'
      : ua.includes('chrome')
        ? 'Chrome'
        : ua.includes('safari')
          ? 'Safari'
          : 'Browser';
  return `${device} · ${browser}`;
}

export function createCollabClient({
  store = defaultStore,
  eventBus = defaultEventBus,
  socketFactory = (url, protocols) => new WebSocket(url, protocols),
  getOrigin,
  getToken,
  ensureHydrated,
  onDivergence = async () => 'adopt',
  onAdoptSnapshot = async () => {},
  onFatalError = () => {},
  onSaveNotify = () => {},
  onOpDropped = () => {},
  storage = typeof globalThis !== 'undefined' ? globalThis.localStorage : null,
  timers = globalThis,
  randomId = () => crypto.randomUUID(),
  jitter = () => Math.random(),
  getDeviceLabel = () => coarseDeviceLabel(),
  backoff = null
} = {}) {
  let socket = null;
  let projectId = null;
  let connected = false;
  let intentionalClose = false;
  let reconnectAttempt = 0;
  let reconnectTimer = 0;
  let pingTimer = 0;
  let syncTimer = 0;
  let socketGeneration = 0;
  let connecting = false;

  let selfConnId = null;
  let roomEpoch = null;
  let serverHead = 0;
  let cloudVersion = null;
  let saveLeaderConnId = null;
  let awaitingCatchup = false;
  let peers = [];
  let locks = [];
  let sentHello = null;
  let acceptedOnWelcome = [];
  let editorClaim = null;
  let identityRepairCapability = null;

  const opCapture = createOpCapture({
    store,
    send: (frame) => rawSend(frame),
    randomId,
    onResyncNeeded: (reason) => triggerResync(reason),
    onOpDropped: (op, info) => onOpDropped(op, info),
    getIdentityRepairContext: () => identityRepairContext()
  });

  // ── storage: persisted lastSeq for reconnect replay ──
  function persistSeq() {
    // Never re-persist while disconnected — a resync clears the replay point
    // precisely so the reconnect adopts a fresh snapshot; the post-dispatch
    // persistSeq() in onMessage must not undo that.
    if (!connected || !storage || !projectId || !roomEpoch) return;
    try {
      storage.setItem(
        seqStorageKey(projectId),
        JSON.stringify({ seq: opCapture.getLastAppliedSeq(), epoch: roomEpoch })
      );
    } catch {
      /* private mode — replay just falls back to a snapshot */
    }
  }
  function readPersistedSeq() {
    if (!storage || !projectId) return null;
    try {
      const raw = storage.getItem(seqStorageKey(projectId));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Number.isInteger(parsed?.seq) && typeof parsed?.epoch === 'string') return parsed;
    } catch {
      /* corrupt — ignore */
    }
    return null;
  }
  function clearPersistedSeq() {
    if (!storage || !projectId) return;
    try {
      storage.removeItem(seqStorageKey(projectId));
    } catch {
      /* ignore */
    }
  }

  // ── low-level send ──
  function rawSend(frame) {
    if (!socket || socket.readyState !== 1 /* OPEN */) return false;
    try {
      socket.send(JSON.stringify(canonicalCollabFrame(frame)));
      return true;
    } catch {
      return false;
    }
  }

  function apiOrigin() {
    return typeof getOrigin === 'function' ? getOrigin() : '';
  }
  function socketUrl() {
    const origin = apiOrigin().replace(/^http/, 'ws');
    return `${origin}/api/projects/${encodeURIComponent(projectId)}/collab`;
  }
  function subprotocols() {
    const token = typeof getToken === 'function' ? getToken() : '';
    return token
      ? [COLLAB_SUBPROTOCOL, `${COLLAB_TOKEN_SUBPROTOCOL_PREFIX}${token}`]
      : [COLLAB_SUBPROTOCOL];
  }

  // ── connection ──
  function connect(nextProjectId) {
    const targetProjectId = nextProjectId || projectId;
    if (!targetProjectId) return;
    const switchingProjects = projectId != null && projectId !== targetProjectId;
    if (switchingProjects) {
      // A deliberate room switch is the one case where optimistic operations
      // belong to the old document and must not survive into the new room.
      persistSeq();
      opCapture.uninstall();
      opCapture.reset(0);
      clearRuntimeState();
    }
    if (reconnectTimer) {
      timers.clearTimeout(reconnectTimer);
      reconnectTimer = 0;
    }
    teardownSocket();
    stopPing();
    stopSync();
    connected = false;
    projectId = targetProjectId;
    intentionalClose = false;
    connecting = true;
    const generation = ++socketGeneration;
    socket = socketFactory(socketUrl(), subprotocols());
    const thisSocket = socket;
    let messageChain = Promise.resolve();
    socket.onopen = () => onOpen(thisSocket, generation, targetProjectId);
    socket.onmessage = (event) => {
      messageChain = messageChain
        .then(() => onMessage(event, thisSocket, generation, targetProjectId))
        .catch((err) =>
          failConnection(
            err,
            'processing a collaboration message',
            thisSocket,
            generation,
            targetProjectId
          )
        );
    };
    // The event carries the close CODE. Dropping it (as this used to) threw away
    // the server's own statement of why the room hung up — see onClose.
    socket.onclose = (event) => onClose(thisSocket, generation, targetProjectId, event);
    socket.onerror = () => {
      /* onclose follows; reconnect is driven there */
    };
  }

  function isCurrent(thisSocket, generation, expectedProjectId) {
    return (
      socket === thisSocket && socketGeneration === generation && projectId === expectedProjectId
    );
  }

  function assertCurrentProject(expectedProjectId) {
    const state = store.getState();
    if (!state || state.projectId !== expectedProjectId) {
      throw new Error(
        `Collaboration lifecycle changed projects while joining ${expectedProjectId}`
      );
    }
    return state;
  }

  function onOpen(thisSocket, generation, expectedProjectId) {
    if (!isCurrent(thisSocket, generation, expectedProjectId)) return;
    const persisted = readPersistedSeq();
    let state;
    try {
      state = assertCurrentProject(expectedProjectId);
    } catch (err) {
      failConnection(
        err,
        'opening the collaboration connection',
        thisSocket,
        generation,
        expectedProjectId
      );
      return;
    }
    sentHello = {
      t: 'hello',
      v: COLLAB_PROTOCOL_VERSION,
      projectId,
      stateHash: state ? canonicalCollabStateHashOf(state) : undefined,
      cloudVersion: Number(state?.cloudVersion) || 0,
      pendingClientOpIds: opCapture.pendingClientOpIds(),
      deviceLabel: String(getDeviceLabel() || 'Another window').slice(0, 80),
      capabilities: { editableIdRepair: EDITABLE_ID_REPAIR_CAPABILITY }
    };
    if (persisted) {
      sentHello.lastSeq = persisted.seq;
      sentHello.roomEpoch = persisted.epoch;
    }
    rawSend(sentHello);
    startPing();
  }

  function startPing() {
    stopPing();
    pingTimer = timers.setInterval(() => rawSend({ t: 'ping' }), LIMITS.pingIntervalMs);
  }
  function stopPing() {
    if (pingTimer) {
      timers.clearInterval(pingTimer);
      pingTimer = 0;
    }
  }

  function startSync() {
    stopSync();
    syncTimer = timers.setInterval(() => {
      if (connected && !awaitingCatchup && opCapture.isSynced(serverHead)) sendSync();
    }, SYNC_INTERVAL_MS);
  }
  function stopSync() {
    if (syncTimer) {
      timers.clearInterval(syncTimer);
      syncTimer = 0;
    }
  }

  function onClose(thisSocket, generation, expectedProjectId, event = null) {
    if (!isCurrent(thisSocket, generation, expectedProjectId)) return;
    const wasConnected = connected;
    connected = false;
    connecting = false;
    stopPing();
    stopSync();
    socket = null;
    if (wasConnected) eventBus.emit('collab:disconnected', { projectId });
    if (event?.code === CLOSE_CODES.CLIENT_UPGRADE_REQUIRED) {
      intentionalClose = true;
      eventBus.emit('collab:error', {
        t: 'error',
        code: 'client_upgrade_required',
        message: 'Reload PageStitch to edit this project’s repaired element identities.'
      });
      return;
    }

    // SB-1: the close CODE is the strongest lifecycle signal in the system —
    // the server states outright that the project was deleted (4404) or that
    // membership was revoked (4403) on an already-authenticated socket. It used
    // to be discarded here (the handler took no event at all), so the client
    // inferred lifecycle from an ambiguous 404 instead of reading the answer it
    // had already been given.
    const { lifecycle, signal } = classifyProjectSignal({
      operation: LIFECYCLE_OPERATION.SOCKET,
      closeCode: event ? event.code : null
    });
    if (lifecycle === LIFECYCLE.UNAVAILABLE) {
      // Deliberately NOT scheduleReconnect(): a deleted project is not a
      // transient failure, and retrying it forever would both hammer the room
      // and keep the UI hoping. Latch so a later close cannot restart the loop.
      intentionalClose = true;
      eventBus.emit('collab:project-unavailable', { projectId: expectedProjectId, signal });
      return;
    }
    if (!intentionalClose) scheduleReconnect();
  }

  function teardownSocket() {
    if (!socket) return;
    try {
      socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
      socket.close();
    } catch {
      /* already closing */
    }
    socket = null;
  }

  function scheduleReconnect() {
    if (reconnectTimer) return;
    let delay;
    if (typeof backoff === 'function') {
      delay = backoff(reconnectAttempt);
    } else {
      const base = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** reconnectAttempt);
      delay = base / 2 + jitter() * (base / 2);
    }
    reconnectAttempt += 1;
    reconnectTimer = timers.setTimeout(() => {
      reconnectTimer = 0;
      if (!intentionalClose) connect();
    }, delay);
  }

  function triggerResync(reason) {
    // Keep the optimistic outbox installed while the socket is down. The next
    // hello advertises its ids; the server reports which were accepted, and a
    // snapshot/catchup rebase preserves only the still-unaccepted edits.
    clearPersistedSeq();
    connected = false;
    connecting = false;
    stopPing();
    stopSync();
    eventBus.emit('collab:resync', { projectId, reason });
    intentionalClose = false;
    socketGeneration += 1;
    teardownSocket();
    scheduleReconnect();
  }

  // ── frame dispatch ──
  async function onMessage(event, thisSocket, generation, expectedProjectId) {
    if (!isCurrent(thisSocket, generation, expectedProjectId)) return;
    let frame;
    try {
      frame = JSON.parse(event.data);
    } catch {
      return;
    }
    if (!frame || typeof frame.t !== 'string') return;
    switch (frame.t) {
      case 'welcome':
        await handleWelcome(frame, thisSocket, generation, expectedProjectId);
        break;
      case 'seed-request':
        await handleSeedRequest(thisSocket, generation, expectedProjectId);
        break;
      case 'seed-wait':
        break;
      case 'catchup':
        handleCatchup(frame);
        break;
      case 'op':
        serverHead = Math.max(serverHead, Number(frame.seq) || 0);
        opCapture.handleServerOp(frame);
        persistSeq();
        break;
      case 'op-rejected':
        opCapture.handleRejected(frame);
        break;
      case 'presence':
        eventBus.emit('collab:presence', frame);
        break;
      case 'lock-state':
        handleLockState(frame);
        break;
      case 'lock-result':
        eventBus.emit('collab:lock-result', frame);
        break;
      case 'peer-joined':
        handlePeerJoined(frame);
        break;
      case 'peer-left':
        handlePeerLeft(frame);
        break;
      case 'save-leader':
        handleSaveLeader(frame);
        break;
      case 'save-notify':
        handleSaveNotify(frame);
        break;
      case 'editor-changed':
        handleEditorChanged(frame);
        break;
      case 'resync':
        triggerResync('server-resync');
        break;
      case 'error':
        eventBus.emit('collab:error', frame);
        break;
      case 'pong':
        break;
      default:
        break;
    }
  }

  async function handleWelcome(frame, thisSocket, generation, expectedProjectId) {
    if (!isCurrent(thisSocket, generation, expectedProjectId)) return;
    selfConnId = frame.selfConnId;
    roomEpoch = frame.roomEpoch;
    serverHead = frame.seq;
    cloudVersion = frame.cloudVersion ?? null;
    saveLeaderConnId = frame.saveLeaderConnId ?? null;
    peers = Array.isArray(frame.peers) ? frame.peers : [];
    locks = Array.isArray(frame.locks) ? frame.locks : [];
    acceptedOnWelcome = Array.isArray(frame.acceptedClientOpIds) ? frame.acceptedClientOpIds : [];
    editorClaim = frame.editorClaim || null;
    identityRepairCapability = frame.capabilities?.editableIdRepair ?? null;
    reconnectAttempt = 0;
    awaitingCatchup = false;

    if (frame.snapshot) {
      if (frame.snapshot.state?.projectId !== expectedProjectId) {
        throw new Error('Collaboration snapshot belongs to a different project');
      }
      const previousState = structuredClone(assertCurrentProject(expectedProjectId));
      const decision = await onDivergence(frame.snapshot);
      if (!isCurrent(thisSocket, generation, expectedProjectId)) return;
      if (decision !== 'adopt') {
        disconnect();
        return;
      }
      await onAdoptSnapshot(frame.snapshot.state, frame.snapshot.seq);
      if (!isCurrent(thisSocket, generation, expectedProjectId)) return;
      assertCurrentProject(expectedProjectId);
      opCapture.discardAccepted(acceptedOnWelcome);
      opCapture.rebasePendingAfterSnapshot(previousState, frame.snapshot.seq);
      serverHead = frame.snapshot.seq;
    } else if (frame.stateMatched === true) {
      // Full-state equality is stronger than a possibly stale persisted replay
      // cursor. Retire optimistic bookkeeping without replaying operations that
      // are already present in this document (especially structural inserts).
      opCapture.reset(frame.seq);
    } else {
      const expectCatchup = sentHello?.lastSeq != null && sentHello.roomEpoch === roomEpoch;
      if (expectCatchup) {
        opCapture.beginReplay(sentHello.lastSeq);
        awaitingCatchup = true;
      } else {
        // With no replay and no snapshot, the server accepted our hello hash:
        // the optimistic document is already byte-equivalent to room head.
        // Retire every pending op rather than re-sending a structural edit that
        // another author may already have made equivalently.
        opCapture.reset(frame.seq);
      }
    }

    connected = true;
    connecting = false;
    opCapture.install();
    if (!awaitingCatchup) opCapture.flushPending();
    persistSeq();
    startSync();
    eventBus.emit('collab:lock-state', { locks });
    eventBus.emit('collab:peers-changed', { peers, selfConnId, saveLeaderConnId });
    eventBus.emit('collab:connected', {
      projectId,
      selfConnId,
      roomEpoch,
      peers,
      saveLeaderConnId,
      editorClaim
    });
    eventBus.emit('collab:editor-changed', { editorClaim, selfConnId, projectId });
  }

  async function handleSeedRequest(thisSocket, generation, expectedProjectId) {
    if (typeof ensureHydrated === 'function') {
      await ensureHydrated();
    }
    if (!isCurrent(thisSocket, generation, expectedProjectId)) return;
    const state = assertCurrentProject(expectedProjectId);
    rawSend({
      t: 'seed',
      state,
      baseCloudVersion: Number(state.cloudVersion) || 0,
      stateHash: stateHashOf(state)
    });
  }

  function handleCatchup(frame) {
    for (const op of frame.ops || []) opCapture.applyCatchupOp(op);
    if (frame.done) {
      awaitingCatchup = false;
      opCapture.discardAccepted(acceptedOnWelcome);
      opCapture.setLastAppliedSeq(serverHead);
      opCapture.flushPending();
      persistSeq();
    }
  }

  function failConnection(err, stage, thisSocket, generation, expectedProjectId) {
    if (!isCurrent(thisSocket, generation, expectedProjectId)) return;
    const error =
      err instanceof Error ? err : new Error(String(err || 'Unknown collaboration error'));
    eventBus.emit('collab:error', {
      t: 'error',
      code: 'client_failure',
      message: error.message,
      stage
    });
    try {
      onFatalError(error, { stage, projectId: expectedProjectId });
    } catch {
      /* reporting must not mask teardown */
    }
    intentionalClose = true;
    connected = false;
    connecting = false;
    socketGeneration += 1;
    opCapture.uninstall();
    stopPing();
    stopSync();
    teardownSocket();
  }

  function clearRuntimeState() {
    connected = false;
    connecting = false;
    selfConnId = null;
    roomEpoch = null;
    serverHead = 0;
    cloudVersion = null;
    saveLeaderConnId = null;
    awaitingCatchup = false;
    peers = [];
    locks = [];
    sentHello = null;
    acceptedOnWelcome = [];
    editorClaim = null;
    identityRepairCapability = null;
  }

  function handleLockState(frame) {
    locks = Array.isArray(frame.locks) ? frame.locks : [];
    eventBus.emit('collab:lock-state', { locks });
  }

  function updateSaveLeader(next) {
    if (next === undefined) return;
    saveLeaderConnId = next;
  }

  function handlePeerJoined(frame) {
    if (frame.peer) {
      peers = peers.filter((p) => p.connId !== frame.peer.connId).concat(frame.peer);
    }
    updateSaveLeader(frame.saveLeaderConnId);
    eventBus.emit('collab:peer-joined', frame);
    eventBus.emit('collab:peers-changed', { peers, selfConnId, saveLeaderConnId });
  }

  function handlePeerLeft(frame) {
    if (frame.peer) peers = peers.filter((p) => p.connId !== frame.peer.connId);
    updateSaveLeader(frame.saveLeaderConnId);
    eventBus.emit('collab:peer-left', frame);
    eventBus.emit('collab:peers-changed', { peers, selfConnId, saveLeaderConnId });
  }

  function handleSaveLeader(frame) {
    updateSaveLeader(frame.saveLeaderConnId);
    eventBus.emit('collab:peers-changed', { peers, selfConnId, saveLeaderConnId });
  }

  function handleSaveNotify(frame) {
    if (Number.isInteger(frame.version)) cloudVersion = frame.version;
    onSaveNotify({ version: frame.version, bySub: frame.bySub, atSeq: frame.atSeq });
  }

  function handleEditorChanged(frame) {
    editorClaim = frame.editorClaim || null;
    eventBus.emit('collab:editor-changed', { editorClaim, selfConnId, projectId });
  }

  // ── public control ──
  function disconnect(opts = {}) {
    intentionalClose = true;
    if (reconnectTimer) {
      timers.clearTimeout(reconnectTimer);
      reconnectTimer = 0;
    }
    reconnectAttempt = 0;
    const finish = () => {
      persistSeq();
      const wasConnected = connected;
      socketGeneration += 1;
      opCapture.uninstall();
      stopPing();
      stopSync();
      teardownSocket();
      clearRuntimeState();
      if (wasConnected) eventBus.emit('collab:disconnected', { projectId, intentional: true });
    };
    if (opts.flush && connected) {
      return flushOutbox(opts.capMs ?? 2000).finally(finish);
    }
    finish();
    return Promise.resolve(true);
  }

  /** Resolve once the outbox is drained + caught up to head, or after capMs. */
  function flushOutbox(capMs = 3000) {
    if (opCapture.isSynced(serverHead) && !awaitingCatchup) return Promise.resolve(true);
    return new Promise((resolve) => {
      const started = Date.now();
      const check = () => {
        if (opCapture.isSynced(serverHead) && !awaitingCatchup) {
          resolve(true);
          return;
        }
        if (Date.now() - started >= capMs) {
          resolve(false);
          return;
        }
        timers.setTimeout(check, 50);
      };
      timers.setTimeout(check, 50);
    });
  }

  function identityRepairContext() {
    if (!connected || socket?.readyState !== 1 || awaitingCatchup)
      return { ok: false, reason: 'collab-disconnected' };
    if (store.getState()?.projectId !== projectId) return { ok: false, reason: 'project-changed' };
    if (identityRepairCapability !== EDITABLE_ID_REPAIR_CAPABILITY)
      return { ok: false, reason: 'collab-upgrade-required' };
    if (editorClaim?.holderConn !== selfConnId) return { ok: false, reason: 'collab-not-editor' };
    return { ok: true, projectId, roomEpoch, atSeq: serverHead };
  }

  function getIdentityRepairReadiness() {
    const context = identityRepairContext();
    if (!context.ok) return context;
    if (!opCapture.isSynced(serverHead)) return { ok: false, reason: 'collab-syncing' };
    return context;
  }

  return {
    connect,
    disconnect,
    flushOutbox,
    getIdentityRepairReadiness,
    sendPresence(frame) {
      return rawSend({ t: 'presence', ...frame });
    },
    sendLock(action, sectionKey) {
      return rawSend({ t: 'lock', action, sectionKey });
    },
    sendSaved(version, atSeq) {
      return rawSend({ t: 'saved', version, atSeq });
    },
    claimEditor(action = 'claim') {
      return rawSend({ t: 'claim-editor', action });
    },
    sendSync,
    isConnected() {
      return connected && socket?.readyState === 1;
    },
    isConnecting() {
      return connecting;
    },
    isActive() {
      return (
        !intentionalClose &&
        Boolean(projectId) &&
        (connected ||
          connecting ||
          Boolean(socket) ||
          Boolean(reconnectTimer) ||
          opCapture.hasPending())
      );
    },
    getProjectId() {
      return projectId;
    },
    getSelfConnId() {
      return selfConnId;
    },
    getRoomEpoch() {
      return roomEpoch;
    },
    getRoomHead() {
      return serverHead;
    },
    getCloudVersion() {
      return cloudVersion;
    },
    getPeers() {
      return peers.slice();
    },
    getLocks() {
      return locks.slice();
    },
    getSaveLeaderConnId() {
      return saveLeaderConnId;
    },
    getEditorClaim() {
      return editorClaim ? { ...editorClaim } : null;
    },
    isEditor() {
      return connected && selfConnId != null && editorClaim?.holderConn === selfConnId;
    },
    isSaveLeader() {
      return (
        connected &&
        socket?.readyState === 1 &&
        selfConnId != null &&
        selfConnId === saveLeaderConnId
      );
    },
    isSynced() {
      return (
        connected && socket?.readyState === 1 && !awaitingCatchup && opCapture.isSynced(serverHead)
      );
    },
    // Test/inspection seam.
    _opCapture() {
      return opCapture;
    }
  };

  function sendSync() {
    const state = store.getState();
    if (
      !connected ||
      awaitingCatchup ||
      !opCapture.isSynced(serverHead) ||
      state?.projectId !== projectId
    )
      return false;
    return rawSend({
      t: 'sync',
      seq: opCapture.getLastAppliedSeq(),
      stateHash: canonicalCollabStateHashOf(state)
    });
  }
}
